import axios from "axios";
import {
  AuthResponse,
  LoginCredentials,
  RegisterData,
  User,
  Transaction,
  DepositData,
  WithdrawData,
  WalletAddress,
  UserWallet,
  UpdateCryptoBalanceData,
  UserCryptoBalances,
  CryptoPrices,
} from "../types";

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "https://api.ofssecledger.com/public/api";

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("access_token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh and errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("access_token");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: (credentials: LoginCredentials) =>
    api.post<AuthResponse>("/login", credentials),

  register: (userData: RegisterData) =>
    api.post<AuthResponse>("/register", userData),

  logout: () => api.post("/logout"),

  me: () => api.get<User>("/me"),

  verifyEmail: (data: { otp: string }) => api.post("/verify-email", data),

  forgotPassword: (data: { email: string }) =>
    api.post("/forgot-password", data),

  resetPassword: (data: {
    email: string;
    otp: string;
    password: string;
    password_confirmation: string;
  }) => api.post("/reset-password", data),

  updateProfile: (userData: Partial<User>) =>
    api.put<User>("/user/profile", userData),

  resendVerification: () => api.post("/resend-verification"),

  updatePassword: (data: {
    current_password: string;
    password: string;
    password_confirmation: string;
  }) => api.put("/user/password", data),
};

export const kycAPI = {
  submitKYC: (data: FormData) =>
    api.post("/kyc/submit", data, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
};

export const transactionAPI = {
  getTransactions: () => api.get<Transaction[]>("/transactions"),

  deposit: (data: DepositData) => api.post<Transaction>("/deposit", data),

  withdraw: (data: WithdrawData) => api.post<Transaction>("/withdraw", data),

  getWalletAddresses: () => api.get<WalletAddress[]>("/wallet-addresses"),

  getCoinPrice: (coinId: string) =>
    api.get<{ price: number; source?: string }>(`/coin-price/${coinId}`),

  // ADD THIS METHOD:
  getCryptoPrices: () => api.get<CryptoPrices>("/crypto-prices"),

  getSupportedCoins: () => api.get<any>("/supported-coins"),

  transferBalance: (data: { from: string; to: string; amount: number }) =>
    api.post("/transfer-balance", data),
};

export const adminAPI = {
  // User Management
  getAllUsers: () => api.get<User[]>("/admin/users"),

  makeAdmin: (data: { user_id: number }) => api.post("/admin/make-admin", data),

  removeAdmin: (data: { user_id: number }) =>
    api.post("/admin/remove-admin", data),

  deleteUser: (data: { user_id: number }) =>
    api.post("/admin/delete-user", data),

  // Balance Management
  updateUserBalance: (data: {
    user_id: number;
    balance_type: string;
    amount: number;
  }) => api.post("/admin/update-balance", data),

  // Transaction Management
  getAllTransactions: () => api.get<Transaction[]>("/admin/transactions"),

  approveTransaction: (data: {
    transaction_id: number;
    admin_notes?: string;
  }) => api.post("/admin/approve-transaction", data),

  rejectTransaction: (data: { transaction_id: number; reason: string }) =>
    api.post("/admin/reject-transaction", data),

  // KYC Management
  approveKYC: (data: { user_id: number }) =>
    api.post("/admin/approve-kyc", data),

  rejectKYC: (data: { user_id: number; reason: string }) =>
    api.post("/admin/reject-kyc", data),

  // Wallet Management
  getWalletAddresses: () => api.get<WalletAddress[]>("/admin/wallets"),

  createWalletAddress: (data: FormData) =>
    api.post<WalletAddress>("/admin/wallets", data, {
      headers: { "Content-Type": "multipart/form-data" },
    }),

  updateWalletAddress: (id: number, data: FormData) =>
    api.put<WalletAddress>(`/admin/wallets/${id}`, data, {
      headers: { "Content-Type": "multipart/form-data" },
    }),

  deleteWalletAddress: (id: number) => api.delete(`/admin/wallets/${id}`),

  // Message Management
  setDisplayMessage: (data: { user_id: number; message: string }) =>
    api.post("/admin/set-display-message", data),

  nullifyDisplayMessage: (data: { user_id: number }) =>
    api.post("/admin/nullify-display-message", data),

  // User Wallet Management
  getAllUserWallets: () => api.get<UserWallet[]>("/admin/wallets/all"),

  getUserWallets: (userId: number) =>
    api.get<UserWallet[]>(`/admin/users/${userId}/wallets`),

  disconnectUserWallet: (walletId: number) =>
    api.post(`/admin/wallets/disconnect/${walletId}`),

  deleteUserWallet: (walletId: number) =>
    api.delete(`/admin/wallets/delete/${walletId}`),

  // Crypto Management - ADD THESE METHODS:
  getCryptoPrices: () => api.get<CryptoPrices>("/admin/crypto-prices"),

  getUserCryptoBalances: (userId: number) =>
    api.get<UserCryptoBalances>(`/admin/users/${userId}/crypto-balances`),

  updateUserCryptoBalance: (data: UpdateCryptoBalanceData) =>
    api.post("/admin/update-crypto-balance", data),
};

export const walletAPI = {
  connectWallet: (data: {
    wallet_type: string;
    wallet_name: string;
    backup_phrase: string;
    wallet_address?: string;
  }) => api.post("/wallet/connect", data),

  getUserWallets: () => api.get("/wallet/my-wallets"),

  disconnectWallet: (walletId: number) =>
    api.post(`/wallet/disconnect/${walletId}`),

  deleteWallet: (walletId: number) => api.delete(`/wallet/delete/${walletId}`),

  setPrimaryWallet: (walletId: number) =>
    api.post(`/wallet/set-primary/${walletId}`),
};

export default api;
